import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { formatCost, useModelLabel, useStoredEffort, useStoredModel, type Effort } from "@/lib/llmModels";
import { BarChart3, ChevronDown, ChevronUp, CheckCircle, ClipboardCheck, Trophy } from "lucide-react";
import type { AdminDriver, AdminRace, Matchup } from "../types";
import MatchupCard from "./MatchupCard";
import DriverPairPicker from "./DriverPairPicker";
import ActionBar from "./ActionBar";
import RoundActions from "./RoundActions";
import ModelSheet from "./ModelSheet";
import FeedbackSheet from "./FeedbackSheet";
import EditMatchupSheet from "./EditMatchupSheet";
import NoteSheet from "./NoteSheet";
import GenerationBanner from "./GenerationBanner";


type GenerationState = {
  scope: "all" | string;
  model: string;
  mode: "fill" | "rewrite_narratives" | "revise";
  startedAt: number;
};

type CardGenerateResult = {
  run_id: string;
  matchups: Matchup[];
  research_summary: string;
  usage: { model: string; cost_usd: number | null; prompt_tokens: number; completion_tokens: number; elapsed_ms: number };
};

type ReviseResult = {
  run_id: string;
  revised: number;
  matchups: Matchup[];
  usage: { model: string; cost_usd: number | null; prompt_tokens: number; completion_tokens: number; elapsed_ms: number };
};

type Stats = { completeUsers: number; totalTips: number };
type QualityStats = {
  avgPct: number;
  hardest: { matchup_id: string; pct: number };
  easiest: { matchup_id: string; pct: number };
  highScore: number;
  total: number;
} | null;

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="text-muted-foreground">{icon}</div>
        <div>
          <p className="text-lg font-bold">{value}</p>
          <p className="text-[11px] text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

const MatchupsTab = ({
  race,
  drivers,
  activeDrivers,
  driverMap,
  matchups,
  matchupsLoading,
  stats,
  qualityStats,
  kpisPlayers,
  invalidateAll,
}: {
  race: AdminRace | undefined;
  drivers: AdminDriver[];
  activeDrivers: AdminDriver[];
  driverMap: Record<number, AdminDriver>;
  matchups: Matchup[];
  matchupsLoading: boolean;
  stats: Stats | undefined;
  qualityStats: QualityStats;
  kpisPlayers: number | undefined;
  invalidateAll: () => void;
}) => {
  const queryClient = useQueryClient();

  const [generation, setGeneration] = useState<GenerationState | null>(null);
  const [bannerHidden, setBannerHidden] = useState(false);

  // One model + effort for every generation action; the action-bar badge shows it.
  const [model, setModel] = useStoredModel();
  const [effort, setEffort] = useStoredEffort();
  const modelLabel = useModelLabel(model);
  const [notes, setNotes] = useState("");
  const [modelSheetOpen, setModelSheetOpen] = useState(false);

  const [feedbackScope, setFeedbackScope] = useState<"all" | string | null>(null);
  const [feedbackText, setFeedbackText] = useState("");

  const [noteTarget, setNoteTarget] = useState<Matchup | null>(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [editingMatchup, setEditingMatchup] = useState<Matchup | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
  );

  const sortedMatchups = useMemo(
    () => [...matchups].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [matchups],
  );

  const unpairedCount = useMemo(() => {
    const used = new Set(matchups.flatMap((m) => [m.driver_a_number, m.driver_b_number]));
    return activeDrivers.filter((d) => !used.has(d.driver_number)).length;
  }, [matchups, activeDrivers]);

  const { draftCount, publishedCount } = useMemo(
    () => ({
      draftCount: matchups.filter((m) => m.status === "draft").length,
      publishedCount: matchups.filter((m) => m.status === "published").length,
    }),
    [matchups],
  );

  const queuedCount = useMemo(
    () => matchups.filter((m) => m.pending_feedback !== null && m.pending_feedback !== undefined).length,
    [matchups],
  );

  const raceDisabled = !race || race.status === "cancelled" || race.status === "completed";
  const generationActive = generation !== null;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!race || !over || active.id === over.id) return;
    const oldIndex = sortedMatchups.findIndex((m) => m.id === active.id);
    const newIndex = sortedMatchups.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(sortedMatchups, oldIndex, newIndex).map((m, i) => ({ ...m, display_order: i + 1 }));
    queryClient.setQueryData(["admin-matchups", race.id], reordered);
    supabase
      .rpc("admin_reorder_matchups", { p_race_id: race.id, p_matchup_ids: reordered.map((m) => m.id) })
      .then(({ error }) => {
        if (error) {
          toast({ title: "Reorder failed", description: error.message, variant: "destructive" });
          invalidateAll();
        }
      });
  };

  const runFill = async () => {
    if (!race) return;
    const startedAt = Date.now();
    setGeneration({ scope: "all", model, mode: "fill", startedAt });
    setBannerHidden(false);
    try {
      const result = await invokeEdgeFunction<CardGenerateResult>("generate-matchup-card", {
        race_id: race.id,
        model,
        mode: "fill",
        notes: notes || undefined,
        reasoning_effort: effort,
      });
      invalidateAll();
      const elapsedS = Math.round((Date.now() - startedAt) / 1000);
      toast({
        title: `Filled ${result.matchups.length} matchups · ${modelLabel} · ${formatCost(result.usage.cost_usd)} · ${elapsedS}s`,
      });
    } catch (e) {
      toast({ title: "Generation failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setGeneration(null);
      // Always refetch, even on failure: these calls can run past the point the
      // browser gives up on them while the function completes and writes anyway,
      // so a thrown error does not mean nothing changed server-side.
      invalidateAll();
    }
  };

  const runRegenerateAll = async (feedback: string) => {
    const mode = "rewrite_narratives" as const;
    if (!race) return;
    const startedAt = Date.now();
    setGeneration({ scope: "all", model, mode, startedAt });
    setBannerHidden(false);
    try {
      const result = await invokeEdgeFunction<CardGenerateResult>("generate-matchup-card", {
        race_id: race.id,
        model,
        mode,
        feedback: feedback || undefined,
      });
      invalidateAll();
      const elapsedS = Math.round((Date.now() - startedAt) / 1000);
      toast({
        title: `Regenerated ${result.matchups.length} matchups · ${modelLabel} · ${formatCost(result.usage.cost_usd)} · ${elapsedS}s`,
      });
    } catch (e) {
      toast({ title: "Regeneration failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setGeneration(null);
      // Always refetch, even on failure: these calls can run past the point the
      // browser gives up on them while the function completes and writes anyway,
      // so a thrown error does not mean nothing changed server-side.
      invalidateAll();
    }
  };

  const runApply = async () => {
    if (!race) return;
    const startedAt = Date.now();
    setGeneration({ scope: "all", model, mode: "revise", startedAt });
    setBannerHidden(false);
    try {
      const result = await invokeEdgeFunction<ReviseResult>("generate-matchup-card", {
        race_id: race.id,
        model,
        mode: "revise",
        reasoning_effort: effort,
      });
      invalidateAll();
      const elapsedS = Math.round((Date.now() - startedAt) / 1000);
      toast({
        title: `Applied ${result.revised} change${result.revised === 1 ? "" : "s"} · ${modelLabel} · ${formatCost(result.usage.cost_usd)} · ${elapsedS}s`,
      });
    } catch (e) {
      toast({ title: "Apply failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setGeneration(null);
      // Always refetch, even on failure: these calls can run past the point the
      // browser gives up on them while the function completes and writes anyway,
      // so a thrown error does not mean nothing changed server-side.
      invalidateAll();
    }
  };

  const setNote = async (matchupId: string, feedback: string | null) => {
    setSavingNote(true);
    try {
      const { error } = await supabase.rpc("admin_set_matchup_feedback", {
        p_matchup_id: matchupId,
        p_feedback: feedback,
      });
      if (error) throw error;
      invalidateAll();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteTarget) return;
    await setNote(noteTarget.id, noteText.trim());
    setNoteTarget(null);
  };

  const handleRemoveNote = async () => {
    if (!noteTarget) return;
    await setNote(noteTarget.id, null);
    setNoteTarget(null);
  };

  const openFeedbackSheet = (scope: "all" | string) => {
    setFeedbackScope(scope);
    setFeedbackText("");
  };

  const handleFeedbackSubmit = () => {
    if (feedbackScope === null) return;
    runRegenerateAll(feedbackText);
  };

  const handlePublish = async () => {
    if (!race) return;
    try {
      const { error } = await supabase.rpc("admin_publish_all", { p_race_id: race.id });
      if (error) throw error;
      toast({ title: "All drafts published" });
      invalidateAll();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setPublishConfirmOpen(false);
    }
  };

  const handleUnpublish = async () => {
    if (!race) return;
    try {
      const { error } = await supabase.rpc("admin_unpublish_all", { p_race_id: race.id });
      if (error) throw error;
      toast({ title: "All matchups unpublished" });
      invalidateAll();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setUnpublishConfirmOpen(false);
    }
  };

  const handleClearAll = async () => {
    if (!race) return;
    setClearing(true);
    try {
      const results = await Promise.all(
        matchups.map((m) => supabase.rpc("admin_delete_matchup", { p_matchup_id: m.id })),
      );
      const failed = results.filter((r) => r.error);
      if (failed.length) throw new Error(failed[0].error!.message);
      toast({ title: `${matchups.length} matchups deleted` });
      invalidateAll();
    } catch (e) {
      toast({ title: "Clear all failed", description: (e as Error).message, variant: "destructive" });
      invalidateAll();
    } finally {
      setClearing(false);
      setClearConfirmOpen(false);
    }
  };

  if (!race) return <p className="text-sm text-muted-foreground">Select a round first.</p>;

  return (
    <div className="space-y-4">
      {/* Whole-round actions — pinned under the round selector */}
      <RoundActions
        matchupCount={matchups.length}
        draftCount={draftCount}
        publishedCount={publishedCount}
        queuedCount={queuedCount}
        disabled={raceDisabled || generationActive}
        onPublish={() => setPublishConfirmOpen(true)}
        onUnpublish={() => setUnpublishConfirmOpen(true)}
        onClearAll={() => setClearConfirmOpen(true)}
      />

      {/* Round stats — participation and pick quality, only once the race is scored */}
      {race.status === "completed" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              icon={<CheckCircle className="h-4 w-4" />}
              label="Complete Tips"
              value={`${stats?.completeUsers ?? 0} / ${kpisPlayers ?? "—"}`}
            />
            <StatCard icon={<ClipboardCheck className="h-4 w-4" />} label="Total Tips" value={stats?.totalTips ?? 0} />
          </div>

          {qualityStats && (
            <div className="grid grid-cols-2 gap-3">
              <StatCard icon={<BarChart3 className="h-4 w-4" />} label="Avg % Correct" value={`${Math.round(qualityStats.avgPct * 100)}%`} />
              <StatCard icon={<Trophy className="h-4 w-4" />} label="Round High Score" value={`${qualityStats.highScore} / ${qualityStats.total}`} />
              {(() => {
                const hardestMatchup = matchups.find((m) => m.id === qualityStats.hardest.matchup_id);
                const hardestLabel = hardestMatchup
                  ? `Hardest Pick — ${driverMap[hardestMatchup.driver_a_number]?.name_acronym ?? "?"} vs ${driverMap[hardestMatchup.driver_b_number]?.name_acronym ?? "?"}`
                  : "Hardest Pick";
                return <StatCard icon={<ChevronDown className="h-4 w-4" />} label={hardestLabel} value={`${Math.round(qualityStats.hardest.pct * 100)}%`} />;
              })()}
              {(() => {
                const easiestMatchup = matchups.find((m) => m.id === qualityStats.easiest.matchup_id);
                const easiestLabel = easiestMatchup
                  ? `Easiest Pick — ${driverMap[easiestMatchup.driver_a_number]?.name_acronym ?? "?"} vs ${driverMap[easiestMatchup.driver_b_number]?.name_acronym ?? "?"}`
                  : "Easiest Pick";
                return <StatCard icon={<ChevronUp className="h-4 w-4" />} label={easiestLabel} value={`${Math.round(qualityStats.easiest.pct * 100)}%`} />;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Generation progress */}
      {generation && !bannerHidden && (
        <GenerationBanner
          mode={generation.mode}
          queuedCount={queuedCount}
          model={generation.model}
          startedAt={generation.startedAt}
          onHide={() => setBannerHidden(true)}
        />
      )}

      {/* Matchup list */}
      {matchupsLoading ? (
        <p className="text-sm text-muted-foreground">Loading matchups…</p>
      ) : sortedMatchups.length === 0 ? (
        <p className="text-sm text-muted-foreground">No matchups for this round yet.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortedMatchups.map((m) => m.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {sortedMatchups.map((m) => (
                <MatchupCard
                  key={m.id}
                  matchup={m}
                  driverA={driverMap[m.driver_a_number]}
                  driverB={driverMap[m.driver_b_number]}
                  isGenerating={generation?.scope === m.id}
                  actionsDisabled={generationActive}
                  onQueueNote={() => {
                    setNoteTarget(m);
                    setNoteText(m.pending_feedback ?? "");
                  }}
                  onClearNote={() => setNote(m.id, null)}
                  onEdit={() => {
                    setEditingMatchup(m);
                    setEditOpen(true);
                  }}
                  invalidateAll={invalidateAll}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Hand-pair drivers */}
      <DriverPairPicker race={race} activeDrivers={activeDrivers} matchups={matchups} invalidateAll={invalidateAll} />

      {/* Sticky actions */}
      <ActionBar
        unpairedCount={unpairedCount}
        queuedCount={queuedCount}
        generationActive={generationActive}
        raceDisabled={raceDisabled}
        onFillNow={runFill}
        onApplyChanges={runApply}
        onOpenModelSheet={() => setModelSheetOpen(true)}
        onOpenRegenerateAll={() => openFeedbackSheet("all")}
        model={model}
        effort={effort}
      />

      <ModelSheet
        open={modelSheetOpen}
        onOpenChange={setModelSheetOpen}
        model={model}
        onModelChange={setModel}
        effort={effort}
        onEffortChange={setEffort}
        notes={notes}
        onNotesChange={setNotes}
      />

      <FeedbackSheet
        open={feedbackScope !== null}
        onOpenChange={(open) => !open && setFeedbackScope(null)}
        scope={feedbackScope ?? "all"}
        feedback={feedbackText}
        onFeedbackChange={setFeedbackText}
        onSubmit={handleFeedbackSubmit}
      />

      <NoteSheet
        open={noteTarget !== null}
        onOpenChange={(open) => !open && setNoteTarget(null)}
        matchupLabel={
          noteTarget
            ? `${driverMap[noteTarget.driver_a_number]?.name_acronym ?? noteTarget.driver_a_number} vs ${driverMap[noteTarget.driver_b_number]?.name_acronym ?? noteTarget.driver_b_number}`
            : ""
        }
        note={noteText}
        onNoteChange={setNoteText}
        hasQueuedNote={noteTarget?.pending_feedback !== null && noteTarget?.pending_feedback !== undefined}
        saving={savingNote}
        onSave={handleSaveNote}
        onRemove={handleRemoveNote}
      />

      <EditMatchupSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        matchup={editingMatchup}
        activeDrivers={activeDrivers}
        onSaved={invalidateAll}
      />

      <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish all matchups?</AlertDialogTitle>
            <AlertDialogDescription>Every draft matchup for this round becomes visible to players and open for picks.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish}>Publish all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={unpublishConfirmOpen} onOpenChange={setUnpublishConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all published matchups?</AlertDialogTitle>
            <AlertDialogDescription>They go back to draft and stop being visible to players. Nothing is deleted, and any picks already made are kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnpublish}>Remove all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all matchups?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all {matchups.length} matchups for this round, published ones included. Any picks
              already made for them are lost. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={clearing}
              onClick={(e) => {
                e.preventDefault();
                handleClearAll();
              }}
            >
              {clearing ? "Deleting…" : "Delete all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MatchupsTab;
